<script setup lang="ts">
import { breakpointsTailwind } from '@vueuse/core'
import type { ITabState } from './types'
import { addTabKey, removeTabKey, stateKey, updateTabCountKey, updateTabKey } from './types'

interface IProps {
  defaultTab?: number
  color?: string
  border?: number
  variant?: 'normal' | 'outline' | 'outline-soft' | 'underline'
  expandTabWidth?: boolean
}

interface IEmit {
  (e: 'selectTab', tab: string): void
}

const props = withDefaults(
  defineProps<IProps>(),
  {
    defaultTab: 0,
    color: useTheme().colors.grayLighter,
    variant: 'normal',
    border: 0,
    expandTabWidth: true,
  },
)
const emit = defineEmits<IEmit>()

const { defaultTab, color } = toRefs(props)

const state = reactive<ITabState>({
  activeTab: defaultTab.value,
  tabs: [],
  counts: {},
})

const breakpoints = useBreakpoints(breakpointsTailwind)
const isMobile = breakpoints.smaller('md')

watch(defaultTab, (newTab) => {
  state.activeTab = newTab
})

provide(stateKey, state)

provide(addTabKey, (tab: string) => {
  state.tabs.push(tab)
})

provide(updateTabKey, (oldTab: string, newTab: string) => {
  const index = state.tabs.indexOf(oldTab)
  state.tabs[index] = newTab
})

provide(removeTabKey, (tab: string) => {
  const index = state.tabs.indexOf(tab)
  state.tabs.splice(index, 1)
})

provide(updateTabCountKey, (tab: string, count?: number) => {
  state.counts[tab] = count
})

function selectTab(tab: string) {
  if (state.tabs?.length) {
    state.activeTab = state.tabs.indexOf(tab)
    emit('selectTab', tab)
  }
}

function tabIsActive(tab: string) {
  return state.activeTab === state.tabs.indexOf(tab)
}
</script>

<template>
  <ul flex="~ nowrap" data-allow-mismatch="class" items-center w-full :class="[$style[variant], isMobile && variant === 'underline' ? `h-[51px] flex-shrink-0 overflow-y-auto` : '']">
    <li
      v-for="tab in state.tabs" :key="tab"
      :class="[
        expandTabWidth ? `w-[${100 / state.tabs.length}%]` : '',
        expandTabWidth ? `flex-1 whitespace-nowrap` : '',
      ]"
    >
      <button
        type="button"
        :b="border && variant !== 'underline' ? 2 : 0"
        :rounded="variant !== 'underline' ? 'full' : '0'"
        class="w-full min-w-max text-bluish-softer h-full"
        :class="[
          variant !== 'underline' && (tabIsActive(tab)
            ? 'border-2 border-gray-lighter !text-black-softer'
            : 'border-2 border-transparent'),
          $style[`${variant}-item`],
          tabIsActive(tab) && $style[`${variant}-item__active`],
        ]"
        :style="{
          backgroundColor: tabIsActive(tab) && variant !== 'underline' ? color : 'transparent',
        }"
        @click="selectTab(tab)"
      >
        <slot :name="`tab-${tab}`">
          <div px-4 flex gap-2 items-center justify-center>
            <Text py="6px">
              {{ tab }}
            </Text>

            <BubbleCount
              v-if="state.counts[tab] !== undefined"
              :is-active="tabIsActive(tab)"
            >
              {{ state.counts[tab] === 10 ? '+' : '' }}{{ state.counts[tab] }}
            </BubbleCount>
          </div>
        </slot>
      </button>
    </li>
  </ul>

  <slot />
</template>

<style lang="scss" module>
.normal {
  --at-apply: overflow-y-hidden overflow-x-auto rounded-full min-h-8;
}

.outline {
  --at-apply: rounded-full border border-gray-lighter bg-gray-lighter;
}
.outline-soft {
  --at-apply: rounded-2 border border-gray-lighter bg-gray-lighter;
}
.outline-soft-item {
  --at-apply: rounded-2;
}

.outline-soft-item__active {
  --at-apply: bg-white;
}

.underline {
  --at-apply: border-b-1 border-gray-lighter;
  margin-bottom: 1px;
}

.underline-item {
  margin-bottom: -1px;
  --at-apply: px-3 md:px-7 py-2 color-bluish-softer border-gray-lighter border-0 border-b-1;
}

.underline-item__active {
  --at-apply: color-soft-black border-soft-black;
}
</style>
